import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="container mx-auto flex-1 px-4 py-8 md:py-12 max-w-3xl">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
          Privacy Policy
        </h1>

        <div className="space-y-8 text-foreground leading-relaxed">
          <section>
            <p className="text-sm text-muted-foreground">
              Last updated: June 2026.
            </p>
            <p className="mt-4">
              MAS Matrimonial (&ldquo;we,&rdquo; &ldquo;us&rdquo;) is a free community
              matrimonial service operated by Muslim American Society DFW. This policy
              explains what information we collect from you, who can see it, and what
              we do with it.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">What we collect</h2>
            <p>
              When you create a profile or request to join, we collect the information
              you provide, which can include:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Your name and age (and date of birth, if provided)</li>
              <li>Citizenship status and current location</li>
              <li>Contact details: email, phone number, WhatsApp number</li>
              <li>Photos you upload to your profile</li>
              <li>Family information you choose to share</li>
              <li>Your preferences for a potential match</li>
              <li>Free-text fields you fill in (bio, &ldquo;seeking,&rdquo; notes to your matchmaker, dealbreaker questions)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">Who sees your information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>You</strong> — you can view and edit your own profile at any time.</li>
              <li><strong>Your matchmaker(s)</strong> — the matchmaker(s) you are enrolled with can see your profile in order to suggest matches and contact you.</li>
              <li><strong>Platform admins</strong> — a small number of administrators can access profiles for moderation, support, and safety.</li>
              <li><strong>Other candidates</strong> — your profile is <em>only</em> shown to another candidate after both of you have accepted a match introduced by a matchmaker. Candidates do not browse each other.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">Third-party services</h2>
            <p>We rely on a small number of vendors to operate the service:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Supabase</strong> — hosts our database, authentication, and file storage (your photos and profile data).</li>
              <li><strong>Resend</strong> — delivers transactional email (magic-link sign-in, notifications).</li>
              <li><strong>Anthropic (via a self-hosted proxy)</strong> — when you paste raw bio-data text into the profile-import tool, that text is sent to an AI model for one-shot extraction into structured fields. The pasted text is processed in the moment and is not retained beyond the response. We also use Anthropic&rsquo;s AI tools during development and debugging of the platform, which may involve reviewing application data to diagnose and fix issues.</li>
              <li>
                <strong>Microsoft Clarity</strong> — we use Clarity to understand how
                people use the site (page views, clicks, scroll depth, and recordings
                of in-page interactions). Clarity automatically masks the values of
                form fields. Clarity also records your IP address and approximate
                location. See Microsoft&rsquo;s privacy notice at{" "}
                <a
                  href="https://privacy.microsoft.com/privacystatement"
                  className="underline"
                  rel="noreferrer"
                  target="_blank"
                >
                  privacy.microsoft.com/privacystatement
                </a>
                .
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">What we do not collect</h2>
            <p>
              We do not collect Social Security numbers, financial account information,
              or biometric data.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">Retention</h2>
            <p>
              We retain your profile and associated data while your account is active.
              When you delete your account, we delete your profile data. Some
              minimal records may persist in backups or operational logs for a
              limited period, after which they are also removed.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">Your rights</h2>
            <p>You can:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>View and edit</strong> your profile from the Settings page in your dashboard.</li>
              <li><strong>Delete</strong> your account and associated profile from the Settings page.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">Changes to this policy</h2>
            <p>
              We may update this policy as the platform evolves. We will notify users of
              any material changes by email.
            </p>
          </section>

          <section>
            <h2 className="text-xl md:text-2xl font-semibold mb-3">Contact</h2>
            <p>
              For questions about this policy or your data, email the founder at{" "}
              <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-sm">
                admin@masmatrimonial.com
              </span>
              .
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
